import moment from "moment";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { confirmVerify, sendVerify } from "../../api";
import { useUpdateEffect } from "react-use";

const VerificationInput = ({
  id,
  type,
  data,
  setData,
  afterSave,
  className,
}) => {
  const queryClient = useQueryClient();
  const [value, setValue] = useState("");
  const [isAvailable, setAvailable] = useState(false);
  const [code, setCode] = useState("");
  const [isSent, setSent] = useState(false);
  const [time, setTime] = useState(0);
  const [isVerified, setVerified] = useState(false);
  const [msg, setMessage] = useState({ info: "", sent: "", verified: "" });

  const { mutate: sendMutation } = useMutation({
    mutationFn: sendVerify,
    onSettled: (res) => {
      if (res && res.result) {
        const { expired_at } = res.data;
        const t = new moment(expired_at).diff(new moment());
        setTime(t);
        setMessage({ ...msg, sent: "인증 번호를 입력하세요." });
        return setSent(true);
      }
      setMessage({ ...msg, sent: res.error });
      setSent(false);

      queryClient.invalidateQueries({ queryKey: ["verify/send"] });
    },
  });

  const { mutate: confirmMutation } = useMutation({
    mutationFn: confirmVerify,
    onSettled: (res) => {
      if (res && res.result) {
        setTime(0);
        setMessage({ ...msg, verified: "인증에 성공하였습니다." });
        setVerified(true);
        setData(value);
        return afterSave ? afterSave({ [`${type}Verified`]: true }) : null;
      }
      setMessage({ ...msg, verified: res.error });

      queryClient.invalidateQueries({ queryKey: ["verify/confirm"] });
    },
  });

  const handleConfirmVerify = () => {
    confirmMutation({ id, code });
  };

  // 전화번호 유효성 검사 함수
  const validateTel = (tel) => {
    return new RegExp("^(010|070)[0-9]{8}$").test(tel);
  };
  // 이메일 유효성 검사 함수
  const validateEmail = (email) => {
    return new RegExp("^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$").test(
      email
    );
  };

  const handleSendVerify = () => {
    sendMutation({ id, [type]: value });
  };

  const handleChange = (e) => {
    const { value: val, id: target } = e.target;
    let v = val.replace(/\s/g, "");
    if (target === "code") {
      v = v.replace(/[^\d]/g, "");
      setCode(v);
      setMessage({ info: "", sent: "", verified: "" });
    } else {
      setAvailable(false);
      setCode("");
      setSent(false);
      if (type === "tel") {
        v = v.replace(/[^0-9]/g, "");
        if (validateTel(v)) {
          if (data !== v) {
            setAvailable(true);
            setMessage({ ...msg, sent: "", info: "" });
          } else {
            setAvailable(false);
            setMessage({
              ...msg,
              sent: "",
              info: "인증된 휴대전화 번호입니다.",
            });
          }
        } else {
          setMessage({
            ...msg,
            sent: "",
            info: "문자 수신이 가능한 번호를 입력하세요.",
          });
        }
      }
      if (type === "email") {
        if (validateEmail(v)) {
          if (data !== v) {
            setAvailable(true);
            setMessage({ ...msg, sent: "", info: "" });
          } else {
            setAvailable(false);
            setMessage({ ...msg, sent: "", info: "인증된 메일 주소입니다." });
          }
        } else {
          setMessage({
            ...msg,
            sent: "",
            info: "메일 수신이 가능한 주소를 입력하세요.",
          });
        }
      }
      setValue(v);
    }
  };

  useUpdateEffect(() => {
    if (isVerified) return;
    setData("");
    setMessage({ info: "", sent: "", verified: "" });
    setAvailable(false);
    setTime(0);
  }, [type]);

  useUpdateEffect(() => {
    let timer;
    if (time) {
      timer = setInterval(() => {
        if (time < 1000) {
          setTime(0);
          clearInterval(timer);
        } else {
          setTime(time - 1000);
        }
      }, 1000);
    } else {
      if (!isVerified) {
        setSent(false);
      }
    }

    return () => {
      timer && clearInterval(timer);
    };
  }, [time]);

  useEffect(() => {
    setValue(data);
  }, [data]);

  return (
    <div className="form-control">
      <input
        type={type}
        placeholder={type === "tel" ? "휴대전화번호" : "이메일주소"}
        className={`input input-bordered mt-4${
          isAvailable ? " border-primary" : ""
        }${isVerified ? " text-primary" : ""} ${className || ""}`}
        onChange={handleChange}
        value={value}
        readOnly={isVerified}
      />
      <div className="flex flex-col mt-1">
        {data && !isAvailable && (
          <span className="text-sm text-red-500 text-left">{msg.info}</span>
        )}
        {value && !isSent && isAvailable && (
          <>
            {msg.sent && (
              <span className="text-sm text-left text-red-500">{msg.sent}</span>
            )}
            <button
              className="btn btn-accent mt-2"
              disabled={!isAvailable}
              onClick={handleSendVerify}
            >
              인증번호 보내기
            </button>
          </>
        )}
        {value && isAvailable && isSent && !isVerified && (
          <>
            <div className="inline-flex items-center gap-2">
              <input
                id="code"
                type="text"
                placeholder="인증번호"
                className={`input input-bordered flex-auto${
                  code.length === 5 ? " border-primary" : ""
                } ${className || ""}`}
                onChange={handleChange}
                value={code}
              />
              <span className="text-primary">
                {new moment(time).format("mm:ss")}
              </span>
            </div>
            <button
              className="btn btn-accent mt-2"
              disabled={!code || code.length !== 5}
              onClick={handleConfirmVerify}
            >
              확인
            </button>
          </>
        )}
        {isVerified && (
          <span className="text-sm text-left text-green-500">
            {msg.verified}
          </span>
        )}
        {isSent && !isVerified && (
          <span className="text-sm text-left text-red-500">{msg.verified}</span>
        )}
        {!value && (
          <>
            <span className="text-sm text-left">
              본인 인증 및 THEMAKER 관련 정보 수신에 사용됩니다.
            </span>
            <span className="text-sm text-left">
              인증코드를 받을 수 있는 휴대전화번호 및 이메일을 입력하세요.
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default VerificationInput;
